import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { MegaMenu, useMegaMenu } from "./MegaMenu";
import { LOCALES, useTranslation } from "../lib/i18n";
import { useIdentity } from "../lib/identity";

const NAV_LINKS = [
  { labelKey: "nav.home", to: "/" },
  { labelKey: "nav.architecture", to: "/architecture" },
  { labelKey: "nav.industries", to: "/industries", mega: "industries" as const },
  { labelKey: "nav.domains", to: "/domains", mega: "domains" as const },
  { labelKey: "nav.certification", to: "/certification", mega: "certifications" as const },
  { labelKey: "nav.licensing", to: "/licensing" },
  { label: "Consultation", to: "/licensing/quote" },
  { labelKey: "nav.downloads", to: "/downloads" },
  { labelKey: "nav.contact", to: "/contact" },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [localeOpen, setLocaleOpen] = useState(false);
  const { openMenu, setOpenMenu } = useMegaMenu();
  const location = useLocation();
  const { locale, setLocale, t } = useTranslation();
  const { identity } = useIdentity();
  const [authed, setAuthed] = useState(identity.isAuthenticated);
  useEffect(() => { setAuthed(identity.isAuthenticated); }, [identity.isAuthenticated]);

  return (
    <header
      onMouseLeave={() => setOpenMenu(null)}
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        background: "rgba(10, 22, 40, 0.85)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <nav
        className="container"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "var(--nav-height)",
        }}
      >
        <Link
          to="/"
          onClick={() => setMobileOpen(false)}
          style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0, marginLeft: "0", marginRight: "1rem" }}
        >
          <SentinelLogo />
          <span
            style={{
              fontWeight: 800,
              fontSize: "1.375rem",
              color: "var(--text-on-dark)",
              letterSpacing: "-0.01em",
            }}
          >
            Sentinel
          </span>
        </Link>

        {/* Desktop nav */}
        <div
          className="nav-desktop"
          style={{ display: "flex", alignItems: "center", gap: "0", flex: 1, justifyContent: "center" }}
        >
          {NAV_LINKS.map((link) =>
            link.mega ? (
              <div
                key={link.to}
                style={{ position: "relative" }}
                onMouseEnter={() => setOpenMenu(link.mega!)}
              >
                <button
                  onClick={() => setOpenMenu(openMenu === link.mega ? null : link.mega)}
                  aria-haspopup="menu"
                  aria-expanded={openMenu === link.mega}
                  style={{
                    padding: "0.5rem 0.5rem",
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    color: openMenu === link.mega ? "var(--accent-400)" : "var(--text-muted-on-dark)",
                    borderRadius: "var(--radius-md)",
                    transition: "color var(--transition-fast)",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t(link.labelKey)}
                </button>
              </div>
            ) : (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setOpenMenu(null)}
                style={({ isActive }) => ({
                  padding: "0.5rem 0.5rem",
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: isActive ? "var(--accent-400)" : "var(--text-muted-on-dark)",
                  borderRadius: "var(--radius-md)",
                  transition: "color var(--transition-fast)",
                  whiteSpace: "nowrap",
                })}
              >
                {link.labelKey ? t(link.labelKey) : link.label}
              </NavLink>
            )
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
          {/* Language selector — desktop only */}
          <div className="nav-desktop-right" style={{ position: "relative" }}>
            <button
              onClick={() => setLocaleOpen((v) => !v)}
              aria-label="Select language"
              style={{
                padding: "0.4rem 0.625rem",
                fontSize: "1rem",
                fontWeight: 700,
                color: "var(--text-muted-on-dark)",
                background: "rgba(255,255,255,0.05)",
                borderRadius: "var(--radius-md)",
                border: "1px solid rgba(255,255,255,0.1)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                lineHeight: 1,
              }}
            >
              <span style={{ fontSize: "1.1rem" }}>
                {LOCALES.find((l) => l.code === locale)?.flag}
              </span>
              <span style={{ fontSize: "0.75rem", fontWeight: 700 }}>
                {locale.toUpperCase()}
              </span>
            </button>
            {localeOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  marginTop: "0.375rem",
                  background: "var(--bg-dark)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "var(--radius-md)",
                  padding: "0.375rem",
                  minWidth: "120px",
                  zIndex: 300,
                }}
              >
                {LOCALES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => {
                      setLocale(l.code);
                      setLocaleOpen(false);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      width: "100%",
                      padding: "0.5rem 0.75rem",
                      fontSize: "0.8125rem",
                      color: l.code === locale ? "var(--accent-400)" : "var(--text-on-dark)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      borderRadius: "var(--radius-sm)",
                    }}
                  >
                    <span style={{ fontSize: "1.1rem", lineHeight: 1 }}>{l.flag}</span>
                    <span>{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Auth-aware buttons — desktop only */}
          {authed ? (
            <>
              <Link
                to="/dashboard"
                onClick={() => setOpenMenu(null)}
                className="btn nav-desktop-right"
                style={{
                  padding: "0.5rem 0.875rem",
                  fontSize: "0.8125rem",
                  fontWeight: 700,
                  background: "var(--primary-600)",
                  color: "#fff",
                  borderRadius: "var(--radius-md)",
                  whiteSpace: "nowrap",
                }}
              >
                Dashboard
              </Link>
              <Link
                to="/admin"
                onClick={() => setOpenMenu(null)}
                className="btn nav-desktop-right"
                style={{
                  padding: "0.5rem 0.875rem",
                  fontSize: "0.8125rem",
                  fontWeight: 700,
                  background: "transparent",
                  color: "var(--accent-400)",
                  border: "1px solid var(--accent-500)",
                  borderRadius: "var(--radius-md)",
                  whiteSpace: "nowrap",
                }}
              >
                Admin
              </Link>
              <Link
                to="/logout"
                onClick={() => setOpenMenu(null)}
                className="btn btn-ghost-light nav-desktop-right"
                style={{ padding: "0.5rem 0.875rem", fontSize: "0.8125rem", whiteSpace: "nowrap" }}
              >
                {t("nav.logout")}
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/login"
                onClick={() => setOpenMenu(null)}
                className="btn btn-ghost-light nav-desktop-right"
                style={{ padding: "0.5rem 0.875rem", fontSize: "0.8125rem", whiteSpace: "nowrap" }}
              >
                {t("nav.login")}
              </Link>
              <Link
                to="/register"
                onClick={() => setOpenMenu(null)}
                className="btn btn-primary nav-desktop-right"
                style={{ padding: "0.5rem 0.875rem", fontSize: "0.8125rem", whiteSpace: "nowrap" }}
              >
                {t("nav.register")}
              </Link>
            </>
          )}
          <button
            className="nav-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            style={{
              display: "none",
              background: "transparent",
              border: "none",
              color: "var(--text-on-dark)",
              fontSize: "1.5rem",
              cursor: "pointer",
              padding: "0.25rem",
            }}
          >
            {mobileOpen ? "\u2715" : "\u2630"}
          </button>
        </div>
      </nav>

      {/* Mega-menu panel — rendered at header level so it spans full width */}
      {openMenu === "industries" && (
        <MegaMenu type="industries" onClose={() => setOpenMenu(null)} />
      )}
      {openMenu === "domains" && (
        <MegaMenu type="domains" onClose={() => setOpenMenu(null)} />
      )}
      {openMenu === "certifications" && (
        <MegaMenu type="certifications" onClose={() => setOpenMenu(null)} />
      )}

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="nav-mobile"
          style={{
            background: "var(--bg-dark)",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            padding: "1rem var(--space-4) 1.5rem",
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            {NAV_LINKS.map((link) =>
              link.mega ? (
                <MobileAccordion key={link.to} label={t(link.labelKey)} menuType={link.mega} onNavigate={() => setMobileOpen(false)} />
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  style={{
                    padding: "0.75rem 0.5rem",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    color: location.pathname === link.to ? "var(--accent-400)" : "var(--text-on-dark)",
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                  }}
                >
                  {link.labelKey ? t(link.labelKey) : link.label}
                </Link>
              )
            )}
          </div>
          {/* Mobile-only auth actions */}
          <div style={{ display: "flex", gap: "0.75rem", marginTop: "1rem", paddingBottom: "0.5rem" }}>
            {authed ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  className="btn"
                  style={{ flex: 1, textAlign: "center", padding: "0.625rem 1rem", fontSize: "0.875rem", fontWeight: 700, background: "var(--primary-600)", color: "#fff", borderRadius: "var(--radius-md)" }}
                >
                  Dashboard
                </Link>
                <Link
                  to="/logout"
                  onClick={() => setMobileOpen(false)}
                  className="btn btn-ghost-light"
                  style={{ flex: 1, textAlign: "center", padding: "0.625rem 1rem", fontSize: "0.875rem" }}
                >
                  {t("nav.logout")}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="btn btn-ghost-light"
                  style={{ flex: 1, textAlign: "center", padding: "0.625rem 1rem", fontSize: "0.875rem" }}
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileOpen(false)}
                  className="btn btn-primary"
                  style={{ flex: 1, textAlign: "center", padding: "0.625rem 1rem", fontSize: "0.875rem" }}
                >
                  {t("nav.register")}
                </Link>
              </>
            )}
          </div>
          {/* Mobile Admin Access */}
          <Link
            to="/admin"
            onClick={() => setMobileOpen(false)}
            className="btn"
            style={{
              width: "100%",
              textAlign: "center",
              padding: "0.625rem 1rem",
              fontSize: "0.875rem",
              fontWeight: 700,
              background: "transparent",
              color: "var(--accent-400)",
              border: "1px solid var(--accent-500)",
              borderRadius: "var(--radius-md)",
              marginBottom: "0.5rem",
            }}
          >
            Admin Access
          </Link>
        </div>
      )}

      <style>{`
        @media (max-width: 1024px) {
          .nav-desktop { display: none !important; }
          .nav-desktop-right { display: none !important; }
          .nav-toggle { display: block !important; }
        }
      `}</style>
    </header>
  );
}

function MobileAccordion({ label, menuType, onNavigate }: { label: string; menuType: "industries" | "domains" | "certifications"; onNavigate?: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const items = menuType === "domains"
    ? DOMAINS_MOBILE
    : menuType === "certifications"
    ? CERTIFICATIONS_MOBILE
    : INDUSTRIES_MOBILE;

  return (
    <div style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
      <button
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          width: "100%",
          padding: "0.75rem 0.5rem",
          fontSize: "0.9375rem",
          fontWeight: 600,
          color: "var(--text-on-dark)",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {label}
        <span style={{ fontSize: "0.75rem" }}>{expanded ? "\u25B2" : "\u25BC"}</span>
      </button>
      {expanded && (
        <div style={{ paddingBottom: "0.5rem" }}>
          {items.map((item) => (
            <Link
              key={item.slug}
              to={item.to}
              onClick={() => { setExpanded(false); onNavigate?.(); }}
              style={{
                display: "block",
                padding: "0.5rem 1rem",
                fontSize: "0.875rem",
                color: "var(--text-muted-on-dark)",
              }}
            >
              {item.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const INDUSTRIES_MOBILE = [...INDUSTRIES_DATA].sort((a, b) => a.name.localeCompare(b.name)).map((i) => ({ slug: i.slug, name: i.name, to: `/industries/${i.slug}` }));
const DOMAINS_MOBILE = [...DOMAINS_DATA].sort((a, b) => a.name.localeCompare(b.name)).map((d) => ({ slug: d.slug, name: d.name, to: `/domains/${d.slug}` }));
const CERTIFICATIONS_MOBILE = [...INDUSTRIES_DATA].sort((a, b) => a.name.localeCompare(b.name)).map((i) => ({ slug: i.slug, name: i.name, to: `/certifications/${i.slug}` }));

// Import data for mobile menu
import { INDUSTRIES as INDUSTRIES_DATA } from "../lib/industries";
import { DOMAINS as DOMAINS_DATA } from "../lib/domains";

function SentinelLogo() {
  return (
    <img
      src="/sentinel-white.png"
      alt="Sentinel logo"
      style={{ width: "40px", height: "40px", objectFit: "contain" }}
    />
  );
}
